/**
 * The English/Arabic switch.
 *
 * Rendered as a two-option segmented control rather than a single toggle button,
 * so the target language is always visible and labelled in its own script --
 * a member who only reads Arabic can find "العربية" without understanding the
 * surrounding English interface.
 */

import { useTranslation } from 'react-i18next'

import { cx } from '@/components/ui'
import { useDirection } from '@/hooks/useDirection'
import { SUPPORTED_LANGUAGES } from '@/stores/languageStore'
import type { Language } from '@/types/api'

const NATIVE_LABELS: Record<Language, string> = {
  en: 'EN',
  ar: 'ع',
}

const FULL_LABELS: Record<Language, string> = {
  en: 'English',
  ar: 'العربية',
}

export const LanguageToggle = () => {
  const { t } = useTranslation()
  const { language, setLanguage } = useDirection()

  return (
    <div
      className="flex items-center rounded-lg border border-border-subtle bg-surface-sunken p-0.5"
      role="group"
      aria-label={t('language.label')}
    >
      {SUPPORTED_LANGUAGES.map((option) => {
        const active = option === language
        return (
          <button
            key={option}
            type="button"
            onClick={() => setLanguage(option)}
            aria-pressed={active}
            title={t('language.switchTo', { language: FULL_LABELS[option] })}
            className={cx(
              'min-w-9 rounded-md px-2 py-1 text-xs font-semibold transition',
              active
                ? 'bg-surface text-brand-strong shadow-sm'
                : 'text-ink-subtle hover:text-ink',
            )}
          >
            <span aria-hidden="true">{NATIVE_LABELS[option]}</span>
            <span className="sr-only">{FULL_LABELS[option]}</span>
          </button>
        )
      })}
    </div>
  )
}
